import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { Directive, ElementRef, HostListener, inject, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { NgbDate, NgbDatepicker, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { IMaskDirective } from 'angular-imask';
import type { AnyMaskedOptions } from 'imask';
import moment, { Moment } from 'moment/moment';
import { Observable } from 'rxjs';
import { MUI_CONFIGURATION } from '../../config';
import { MuiI18nProvider } from '../../i18n';
import { MUI_DATEPICKER_I18N, MuiDatepickerI18n } from './providers/mui-datepicker-i18n';
import { ISO8601String, MuiDatepickerV2Format } from './supported-formats';
import { iso8601StringToNgbDate, momentToNgbDate } from './utility';


@Directive()
export abstract class MuiDatePickerBase<Model> implements OnInit, ControlValueAccessor {

  /**
   * The unique identifier of the HTML element. Should be automatically generated if not provided.
   * @type {string}
   */
  @Input() abstract id: string;

  @Input() label?: string;
  @Input() name: string | null = null;
  @Input() animation = true;
  @Input() minDate?: ISO8601String;
  @Input() maxDate?: ISO8601String;

  @Input()
  set format(format: MuiDatepickerV2Format) {
    this._format = format;
  }

  get format(): MuiDatepickerV2Format {
    return this._format ?? this._muiConfig.dateFormat;
  }

  @Input()
  set disableFuture(value: boolean) {
    if (coerceBooleanProperty(value)) {
      this.maxDate = this.maxDate ?? moment().toISOString();
    }
  }

  @Input()
  set disablePast(value: boolean) {
    if (coerceBooleanProperty(value)) {
      this.minDate = this.minDate ?? moment().toISOString();
    }
  }

  @Input()
  set showWeekNumbers(value: boolean) {
    this._showWeekNumbers = coerceBooleanProperty(value);
  }

  @Input()
  set displayMonths(value: unknown) {
    this._displayMonths = coerceNumberProperty(value, 1);
  }

  @Input()
  set useFormatAsPlaceholder(value: boolean) {
    this._useFormatAsPlaceholder = coerceBooleanProperty(value);
  }

  @Input()
  set placeholder(value: string | null) {
    this._placeholder = value;
  }

  get placeholder(): string | null {
    if (this._useFormatAsPlaceholder) {
      return this.getLocalizedFormatPlaceholder();
    }

    return this._placeholder;
  }

  get disabled(): boolean {
    return this._disabled;
  }

  get required(): boolean {
    return this._control?.control?.hasValidator(Validators.required) ?? false;
  }

  get inputId(): string {
    return `${this.id}__input`;
  }

  protected get datepickerId(): string {
    return `${this.id}__datepicker`;
  }

  protected get hasError(): boolean {
    return (this._control?.control) ? this._control.control.invalid && this._control.control.touched : false;
  }

  /**
   * The date the datepicker should navigate to when it is opened. Usually the current date or the value of the input.
   * @returns {moment.Moment | null}
   * @protected
   */
  protected abstract getNavigationDate(): Moment | null;

  @ViewChild('input', { read: ElementRef, static: true })
  protected _inputElement?: ElementRef<HTMLInputElement>;

  @ViewChild(NgbPopover, { static: true })
  protected _popover!: NgbPopover;

  @ViewChild(IMaskDirective, { static: true })
  protected _mask!: IMaskDirective<AnyMaskedOptions>;

  protected _disabled = false;
  protected _showWeekNumbers = false;
  protected _displayMonths = 1;
  protected _maskOptions: AnyMaskedOptions | null = null;
  protected onChange: (newValue: Model | null) => void = () => { /* noop */ };
  protected onTouched: () => void = () => { /* noop */ };
  protected _datepicker: NgbDatepicker | null = null;
  protected _control: NgControl | null = null;
  protected _format?: MuiDatepickerV2Format;
  protected _placeholder: string | null = null;
  protected _useFormatAsPlaceholder = false;
  protected readonly iso8601StringToNgbDate = iso8601StringToNgbDate;
  protected readonly translations$: Observable<{ clearButton: string }>;
  protected readonly _muiConfig = inject(MUI_CONFIGURATION);
  protected readonly _elementRef = inject(ElementRef);
  protected readonly _injector = inject(Injector);
  protected readonly _i18n = inject(MuiI18nProvider);
  protected readonly _datepickerI18n: MuiDatepickerI18n = inject(MUI_DATEPICKER_I18N);

  protected constructor() {
    this.translations$ = this._i18n.datepicker();
  }

  ngOnInit(): void {
    // We need to inject NgControl in the ngOnInit lifecycle hook to prevent a circular dependency error. See:
    // https://stackoverflow.com/questions/39809084/injecting-ngcontrol-in-custom-validator-directive-causes-cyclic-dependency
    this._control = this._injector.get(NgControl, null);
  }

  /**
   * Updates the value of the input element, the datepicker popover and the internal model.
   * @param value The new value to set.
   */
  abstract writeValue(value: Model | null): void;

  registerOnChange(fn: (newValue: Model | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  clear(): void {
    this.resetModel();
    this.clearHTMLInput();
    this.close();
  }

  open(): void {
    this._popover.open();
  }

  close(): void {
    this._popover.close(false);
  }

  /**
   * Reset the model to its null-ish state.
   * @protected
   */
  protected abstract resetModel(): void;

  protected abstract getParserFormat(): MuiDatepickerV2Format | MuiDatepickerV2Format[];

  protected parseDate(date: string | null, strict = false): Moment {
    if (!date) {
      return moment.invalid({ empty: true });
    }

    const format = this.getParserFormat();
    return moment(date, format, strict);
  }

  protected setDatepicker(datepicker: NgbDatepicker | null): void {
    this._datepicker = datepicker;
    const dateForNavigation = this.getNavigationDate();
    if (dateForNavigation && datepicker) {
      datepicker.navigateTo(momentToNgbDate(dateForNavigation));
    }
  }

  protected onInputType(event: Event): void {
    this.focusDate((<HTMLInputElement>event.target).value);
  }

  protected onInputChange(event: Event, strict: boolean): void {
    const value = (<HTMLInputElement>event.target).value;
    const placeholder = this.getLocalizedFormatPlaceholder();
    this.updateModel(value === placeholder ? '' : value, strict);
  }

  protected onInputBlur(event: FocusEvent): void {
    const resetValues = [this.getLocalizedFormatPlaceholder(), this.format, ''];

    if (resetValues.includes((<HTMLInputElement>event.target).value)) {
      this.clearHTMLInput();
      this.destroyMask();
    }

    if (!this._popover.isOpen() || this.elementIsContainedInHost(<HTMLElement>event.relatedTarget)) {
      return;
    }

    this.close();
  }

  protected abstract setupMaskOptions(): void;

  protected onInputFocus(): void {
    this.setupMaskOptions();
    this.open();

    setTimeout(() => {
      // Adjust the cursor position when the input content has been updated by the mask directive
      this._mask?.maskRef?.alignCursorFriendly();
    }, 0);
  }

  protected focusDate(value: string): void {
    const date = this.parseDate(value, false);
    this._datepicker?.focusDate(momentToNgbDate(date));
  }

  /**
   * Update the internal model based on the input value when the mask is completely filled.
   * @param {string} value The input value.
   * @param {boolean} strict Whether to use strict parsing.
   * @protected
   */
  protected abstract updateModel(value: string, strict: boolean): void;

  /**
   * Update the internal model and value of the input element when a date has been selected in the datepicker.
   * @param {NgbDate} value
   * @protected
   */
  protected abstract onDateSelect(value: NgbDate): void;

  protected destroyMask(): void {
    this._mask.maskRef?.destroy();
    this._mask.destroyMask();
    this._maskOptions = null;
  }

  /**
   * Returns the localized format placeholder for the input element.
   * @returns {string} The localized format placeholder.
   * @protected
   */
  protected abstract getLocalizedFormatPlaceholder(): string;

  protected elementIsContainedInHost(element: HTMLElement | null): boolean {
    element = element ?? <HTMLElement>document.activeElement;

    if (!element) {
      return false;
    }

    const host = this._elementRef.nativeElement;
    const popoverElement = document.getElementById(this.datepickerId)?.closest('.mui-datepicker__popover') ?? null;

    if (element === host || element === popoverElement) {
      return true;
    }

    if (element.parentElement) {
      return this.elementIsContainedInHost(element.parentElement);
    }

    return false;
  }

  /**
   * Closes the popover when the user clicks outside the datepicker component. We need to check if the click was performed on
   * either the host element or the popover element, and if not, close the popover.
   * @param {MouseEvent} event
   * @private
   */
  @HostListener('document:mousedown', ['$event'])
  private onDocumentClick(event: MouseEvent | FocusEvent): void {
    if (!this._popover.isOpen() || this.elementIsContainedInHost(<HTMLElement>event.target)) {
      return;
    }

    this.close();
  }

  private clearHTMLInput(): void {
    if (this._inputElement) {
      this._inputElement.nativeElement.value = '';
      this._mask?.maskRef?.updateValue();
    }
  }
}
